import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Tabs,
    Tab,
    IconButton,
    useTheme,
    useMediaQuery,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ShareIcon from '@mui/icons-material/Share';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePWAStatus, useAddToHomeScreenSupport } from '../../hooks/usePWAStatus';

const BookmarkTutorial = ({ open, onClose }) => {
    const [activeTab, setActiveTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isPWA, displayMode } = usePWAStatus();
    const supportsAddToHomeScreen = useAddToHomeScreenSupport();

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const iosSteps = [
        {
            label: 'Open Safari Browser',
            content: 'Make sure you are using Safari browser on your iPhone or iPad. Other browsers may not support this feature.'
        },
        {
            label: 'Navigate to the Website',
            content: 'Go to the Connect App 365 website to bookmark.'
        },
        {
            label: 'Tap the Menu Button',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        Click the three horizontal dots (•••) on the toolbar of Safari. If this button isn't there, skip this step and move to step 4.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <MoreVertIcon color="primary" sx={{ transform: 'rotate(90deg)' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Menu button</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Tap Share',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        In the menu that appears, tap "Share" option.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <IosShareIcon color="primary" />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Share</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Tap More',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        In the share sheet, look for "More" at the bottom of the options and tap it.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <MoreVertIcon color="primary" sx={{ transform: 'rotate(90deg)' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>More</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Find "Add to Home Screen"',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        Now you'll see "Add to Home Screen" in the list of options. Tap it.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <AddIcon color="primary" />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Add to Home Screen</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Customize and Add',
            content: 'You can edit the name of the bookmark if you want, then tap "Add" in the top right corner.'
        },
        {
            label: 'Done!',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        The website icon will now appear on your home screen like a regular app!
                    </Typography>
                </Box>
            )
        }
    ];

    const androidSteps = [
        {
            label: 'Open Chrome Browser',
            content: 'Make sure you are using Google Chrome browser on your Android device for the best experience.'
        },
        {
            label: 'Navigate to the Website',
            content: 'Go to the Connect App 365 to bookmark.'
        },
        {
            label: 'Tap the Menu Button',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        Look for the three dots (menu) button in the top right corner of Chrome.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <MoreVertIcon color="primary" />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Menu button</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Select "Add to Home screen"',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        In the menu, look for "Add to Home screen" option and tap it.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                        <AddIcon color="primary" />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Add to Home screen</Typography>
                    </Box>
                </Box>
            )
        },
        {
            label: 'Customize and Install',
            content: 'You can edit the name of the shortcut, then tap "Add" or "Install" to confirm.'
        },
        {
            label: 'Done!',
            content: (
                <Box>
                    <Typography variant="body2" paragraph>
                        The website shortcut will now appear on your home screen like a regular app!
                    </Typography>
                </Box>
            )
        }
    ];

    const StepperComponent = ({ steps }) => (
        <Stepper orientation="vertical" sx={{ mt: 2 }}>
            {steps.map((step, index) => (
                <Step key={index} active={true} completed={false}>
                    <StepLabel>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {step.label}
                        </Typography>
                    </StepLabel>
                    <StepContent>
                        <Box sx={{ pb: 2 }}>
                            {typeof step.content === 'string' ? (
                                <Typography variant="body2" color="text.secondary">
                                    {step.content}
                                </Typography>
                            ) : (
                                step.content
                            )}
                        </Box>
                    </StepContent>
                </Step>
            ))}
        </Stepper>
    );

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            fullScreen={isMobile}
            scroll="body"
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {isPWA ? 'App Successfully Added!' : 'Add to Home Screen'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isPWA 
                        ? `Great! You're now using Connect App 365 as a standalone app (${displayMode} mode).`
                        : 'Follow these steps to add Connect App 365 to your device\'s home screen for quick access'
                    }
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Show success message if already running as PWA */}
                {isPWA && (
                    <Alert 
                        severity="success" 
                        icon={<CheckCircleIcon />}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2">
                            <strong>Success!</strong> You're already using Connect App 365 as a web app. 
                            You can access it directly from your home screen anytime!
                        </Typography>
                    </Alert>
                )}

                {/* Show warning if device doesn't support add to home screen */}
                {!isPWA && !supportsAddToHomeScreen && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Your current browser may not support adding to home screen. 
                            For the best experience, try using Safari on iOS or Chrome on Android.
                        </Typography>
                    </Alert>
                )}

                {!isPWA && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                            <Tab 
                                icon={<PhoneIphoneIcon />} 
                                label="iOS (iPhone/iPad)" 
                                iconPosition="start"
                            />
                            <Tab 
                                icon={<PhoneAndroidIcon />} 
                                label="Android" 
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>
                )}

                {!isPWA && activeTab === 0 && (
                    <Box>
                        <Typography variant="body1" fontWeight="bold" gutterBottom>
                            For iPhone and iPad users:
                        </Typography>
                        <StepperComponent steps={iosSteps} />
                    </Box>
                )}

                {!isPWA && activeTab === 1 && (
                    <Box>
                        <Typography variant="body1" fontWeight="bold" gutterBottom>
                            For Android users:
                        </Typography>
                        <StepperComponent steps={androidSteps} />
                    </Box>
                )}

                {!isPWA && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.primary">
                            <strong>Tip:</strong> Once added to your home screen, you can tap the icon to open this website 
                            directly without needing to open a browser first!
                        </Typography>
                    </Box>
                )}

                {isPWA && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="success.dark">
                            <strong>You're all set!</strong> Connect App 365 is now running as a standalone app. 
                            You can enjoy a native app-like experience with:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            <Typography component="li" variant="body2" color="success.dark">
                                Full-screen experience without browser UI
                            </Typography>
                            <Typography component="li" variant="body2" color="success.dark">
                                Faster loading times
                            </Typography>
                            <Typography component="li" variant="body2" color="success.dark">
                                Direct access from your home screen
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained" color="primary">
                    Got it!
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BookmarkTutorial;